import { useAppDispatch } from '../shared/hooks/use-app-dispatch';
import { proxyAppAction } from '../actions';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import type { GlobalsTools } from '../shared/types';

interface MicroServiceTools {
  apiClient: GlobalsTools['apiClient'];
  logError: (error: unknown, extraTags?: Record<string, unknown>) => void;
  trackMethod: GlobalsTools['trackMethod'];
  eventBus: {
    on: GlobalsTools['eventBus']['on'];
    off: GlobalsTools['eventBus']['off'];
    emit: (eventName: string, payload?: Record<string, unknown>) => void;
  };
  onAction: (action: unknown) => void;
}

const defaultCache = new Map<string, MicroServiceTools>();

function initialize(
  serviceName: string,
  tools: GlobalsTools,
  dispatch: ReturnType<typeof useAppDispatch>,
  cache: Map<string, MicroServiceTools>
): MicroServiceTools {
  const cached = cache.get(serviceName);
  if (cached) {
    return cached;
  }

  const { apiClient, trackMethod } = tools;
  const proxyAction = (
    proxyAppAction as () => (
      serviceName: string,
      action: unknown
    ) => unknown | null
  )();

  const onAction = (action: unknown) => {
    const proxied = proxyAction(serviceName, action);
    if (proxied) {
      dispatch(proxied as Parameters<typeof dispatch>[0]);
    }
  };

  const logError = (
    error: unknown,
    extraTagsFromMicroApp: Record<string, unknown> = {}
  ) => {
    tools.errorLogger.log(
      error instanceof Error ? error : new Error(String(error)),
      {
        tags: {
          app: serviceName,
        },
      },
      extraTagsFromMicroApp
    );
  };

  const eventBus = {
    on: tools.eventBus.on,
    off: tools.eventBus.off,
    emit: (eventName: string, payload: Record<string, unknown> = {}) => {
      tools.eventBus.emit(eventName, {
        payload,
        metadata: { appName: serviceName, event: eventName },
      });
    },
  };

  const cachedTools: MicroServiceTools = {
    apiClient,
    logError,
    trackMethod,
    eventBus,
    onAction,
  };

  cache.set(serviceName, cachedTools);

  return cachedTools;
}

function useMicroServicesTools(
  serviceName: string,
  cache: Map<string, MicroServiceTools> = defaultCache
): MicroServiceTools {
  const tools = useGlobals();
  const dispatch = useAppDispatch();

  return initialize(serviceName, tools, dispatch, cache);
}

export default useMicroServicesTools;
